import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import {useDispatch} from 'react-redux';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import DialogClose from 'components/DialogClose/styles';
import Textarea from 'components/FieldTextarea/style';
import Loading from 'components/shared/Utils/Loading';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {ReactComponent as IconLockOutline} from 'assets/feedback/icon-lock-outline.svg';
import {ReactComponent as IconSendOutline} from 'assets/feedback/icon-send-outline.svg';
import {SEND_FEEDBACK_REPLY} from 'store/feedback/action';
import {
  useFeedbackDiscount,
  useFeedbackReplySending,
  useFeedbackReplySent
} from 'store/feedback/selectors';
import {FEEDBACK_REPLY_TEMPLATES, FEEDBACK_SENTIMENT} from './mockData';
import {buildPromoSentence} from './promoUtils';
import {
  ReplyModalActionsRow,
  ReplyModalCancelButton,
  ReplyModalContent,
  ReplyModalField,
  ReplyModalFieldLabel,
  ReplyModalFieldValue,
  ReplyModalHeader,
  ReplyModalHelper,
  ReplyModalSubjectInput,
  ReplyModalTextareaWrapper,
  ReplyModalTitle,
  ReplyModalToChip,
  ReplyModalToChipAvatar,
  ReplyModalToChipEmail,
  ReplyModalToChipLock,
  ReplyTemplate,
  ReplyTemplatesRow,
  SendReplyButton
} from './styles';

const SENDER_EMAIL = 'support@getsauce.com';

const ReplySchema = Yup.object().shape({
  subject: Yup.string().trim().required('Subject is required'),
  body: Yup.string().trim().required('Reply cannot be empty')
});

const sentimentFor = (rating) =>
  rating <= 3 ? FEEDBACK_SENTIMENT.NEGATIVE : FEEDBACK_SENTIMENT.POSITIVE;

const avatarInitial = (name) => (name ? name.charAt(0).toUpperCase() : '?');

const PAPER_PROPS = {
  style: {
    borderRadius: 20,
    width: 640,
    maxWidth: '95vw',
    overflow: 'hidden'
  }
};

const ReplyModal = ({open, onClose, review, restaurantName, orderItems}) => {
  const dispatch = useDispatch();
  const sending = useFeedbackReplySending(review?.id);
  const replySent = useFeedbackReplySent(review?.id);
  const feedbackDiscount = useFeedbackDiscount();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [baselineSentAt, setBaselineSentAt] = useState(null);

  const templates = useMemo(
    () => (review ? FEEDBACK_REPLY_TEMPLATES[sentimentFor(review.rating)] : []),
    [review?.rating]
  );

  const [activeTemplateId, setActiveTemplateId] = useState(
    templates[0]?.id || null
  );

  useEffect(() => {
    setActiveTemplateId(templates[0]?.id || null);
  }, [review?.id]);

  useEffect(() => {
    if (open) setHasSubmitted(false);
  }, [open, review?.id]);

  useEffect(() => {
    if (
      hasSubmitted &&
      !sending &&
      replySent?.sentAt &&
      replySent.sentAt !== baselineSentAt
    ) {
      setHasSubmitted(false);
      onClose();
    }
  }, [hasSubmitted, sending, replySent, baselineSentAt, onClose]);

  const selectedTemplate = useMemo(
    () =>
      templates.find((t) => t.id === activeTemplateId) || templates[0] || null,
    [templates, activeTemplateId]
  );

  const customerEmail = review?.contact?.email || '';
  const customerName = review?.customerName || '';
  const firstName = customerName.split(' ')[0] || 'there';

  const promoSentence = useMemo(
    () =>
      review
        ? buildPromoSentence(feedbackDiscount, sentimentFor(review.rating))
        : null,
    [feedbackDiscount, review]
  );

  const initialBody = useMemo(() => {
    if (!selectedTemplate || !review) return '';
    const base = selectedTemplate.text({
      ...review,
      customerName,
      order: {items: orderItems}
    });
    return promoSentence ? `${base} ${promoSentence}` : base;
  }, [selectedTemplate, review, customerName, orderItems, promoSentence]);

  const subjectPrefill = restaurantName
    ? `${restaurantName} — review follow-up`
    : 'Review follow-up';

  const fromLine = restaurantName
    ? `${restaurantName} <${SENDER_EMAIL}>`
    : SENDER_EMAIL;

  return (
    <Formik
      enableReinitialize
      initialValues={{subject: subjectPrefill, body: initialBody}}
      validationSchema={ReplySchema}
      onSubmit={(values) => {
        if (!review?.id) return;
        setBaselineSentAt(replySent?.sentAt || null);
        dispatch(
          SEND_FEEDBACK_REPLY({
            reviewId: review.id,
            subject: values.subject.trim(),
            body: values.body.trim(),
            customerEmail
          })
        );
        setHasSubmitted(true);
      }}
    >
      {({values, handleChange, setFieldValue, submitForm, errors, touched}) => {
        const handleTemplateSwitch = (templateId) => {
          const next = templates.find((t) => t.id === templateId);
          if (!next) return;
          setActiveTemplateId(templateId);
          const base = next.text({
            ...review,
            customerName,
            order: {items: orderItems}
          });
          setFieldValue(
            'body',
            promoSentence ? `${base} ${promoSentence}` : base
          );
        };

        return (
          <Dialog
            open={open}
            onClose={onClose}
            scroll='body'
            PaperProps={PAPER_PROPS}
          >
            <ReplyModalContent>
              <ReplyModalHeader>
                <ReplyModalTitle>{`Reply to ${firstName}`}</ReplyModalTitle>
                <DialogClose onClick={onClose} />
              </ReplyModalHeader>

              <Form>
                <ReplyModalField>
                  <ReplyModalFieldLabel>To</ReplyModalFieldLabel>
                  <ReplyModalFieldValue>
                    <ReplyModalToChip>
                      <ReplyModalToChipAvatar>
                        {avatarInitial(customerName)}
                      </ReplyModalToChipAvatar>
                      <ReplyModalToChipEmail>
                        {customerEmail}
                      </ReplyModalToChipEmail>
                      <ReplyModalToChipLock aria-hidden='true'>
                        <IconLockOutline />
                      </ReplyModalToChipLock>
                    </ReplyModalToChip>
                  </ReplyModalFieldValue>
                </ReplyModalField>

                <ReplyModalField>
                  <ReplyModalFieldLabel>From</ReplyModalFieldLabel>
                  <ReplyModalFieldValue>
                    <span>{fromLine}</span>
                    <ReplyModalHelper>
                      Replies go to your inbox
                    </ReplyModalHelper>
                  </ReplyModalFieldValue>
                </ReplyModalField>

                <ReplyModalField>
                  <ReplyModalFieldLabel>Subject</ReplyModalFieldLabel>
                  <ReplyModalFieldValue>
                    <ReplyModalSubjectInput
                      type='text'
                      name='subject'
                      aria-label='Subject'
                      aria-invalid={!!(errors.subject && touched.subject)}
                      value={values.subject}
                      onChange={handleChange}
                      className={cn({
                        error: errors.subject && touched.subject
                      })}
                    />
                    {errors.subject && touched.subject && (
                      <ErrorSignWrapper>
                        <ErrorSign>{errors.subject}</ErrorSign>
                      </ErrorSignWrapper>
                    )}
                  </ReplyModalFieldValue>
                </ReplyModalField>

                <ReplyModalTextareaWrapper>
                  <Textarea
                    name='body'
                    aria-label='Reply message'
                    aria-invalid={!!(errors.body && touched.body)}
                    className={cn('FieldTextareaLg', {
                      error: errors.body && touched.body
                    })}
                    style={{
                      width: '100%',
                      minHeight: 140,
                      resize: 'vertical'
                    }}
                    value={values.body}
                    onChange={handleChange}
                  />
                  {errors.body && touched.body && (
                    <ErrorSignWrapper>
                      <ErrorSign>{errors.body}</ErrorSign>
                    </ErrorSignWrapper>
                  )}
                </ReplyModalTextareaWrapper>

                <ReplyTemplatesRow>
                  {templates.map((template) => (
                    <ReplyTemplate
                      key={template.id}
                      type='button'
                      data-label={template.label}
                      className={cn({
                        isActive: template.id === (selectedTemplate?.id || null)
                      })}
                      onClick={() => handleTemplateSwitch(template.id)}
                    >
                      {template.label}
                    </ReplyTemplate>
                  ))}
                </ReplyTemplatesRow>

                <ReplyModalActionsRow>
                  <SendReplyButton
                    type='button'
                    onClick={submitForm}
                    disabled={sending}
                  >
                    <IconSendOutline />
                    Send reply
                  </SendReplyButton>
                  <ReplyModalCancelButton
                    type='button'
                    onClick={onClose}
                    disabled={sending}
                  >
                    Cancel
                  </ReplyModalCancelButton>
                </ReplyModalActionsRow>
              </Form>
            </ReplyModalContent>
            {sending && <Loading className='AdjustModal' />}
          </Dialog>
        );
      }}
    </Formik>
  );
};

ReplyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  review: PropTypes.object,
  restaurantName: PropTypes.string,
  orderItems: PropTypes.arrayOf(PropTypes.string)
};

ReplyModal.defaultProps = {
  review: null,
  restaurantName: '',
  orderItems: []
};

export default ReplyModal;
